/**
 * @fileoverview HTTP Client Service
 * @description Centralized HTTP client with interceptors, error handling, and authentication
 * @version 1.0.0
 * @author CRM Development Team
 */

import axios from 'axios';
import { toast } from 'react-toastify';
import { getAccessToken, getRefreshToken, createTokenRefreshService, storeTokens, clearTokens } from '@platform/auth-sdk';
import { convertJsonToFormData } from '../utils/tools';

// Configuration
const API_CONFIG = {
  baseURL: import.meta.env.VITE_API_URL,
  timeout: parseInt(import.meta.env.VITE_API_TIMEOUT) || 30000,
  retryAttempts: 3,
  retryDelay: 1000
};

// Configure axios defaults
axios.defaults.baseURL = API_CONFIG.baseURL;
axios.defaults.timeout = API_CONFIG.timeout;

/**
 * HTTP Status Codes
 */
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500
};

/**
 * Request configuration validator
 * @param {Object} params - Request parameters
 * @throws {Error} When invalid parameters are found
 */
const validateRequestParams = (params) => {
  if (!params) return;
  
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === 'undefined') {
      throw new Error(`Invalid parameter: ${key} is ${value}`);
    }
  }
};

/**
 * Core HTTP request handler
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @param {Object} options - Request options
 * @param {Object} options.params - Query parameters
 * @param {Object} options.payload - Request body
 * @returns {Promise} Axios response promise
 */
const request = async (method, url, options = {}) => {
  const { params = {}, payload = {} } = options;
  
  try {
    validateRequestParams(params);
    
    const config = {
      url,
      method: method.toUpperCase(),
      params,
      data: payload,
      withCredentials: true,
      metadata: {
        timestamp: new Date().toISOString(),
        requestId: Math.random().toString(36).substr(2, 9)
      }
    };

    if (process.env.NODE_ENV === 'development') {
      console.log(`[${method}] ${url}:`, {
        params,
        payload: method !== 'GET' ? payload : undefined
      });
    }

    const response = await axios.request(config);
    
    // Handle success notifications for non-GET requests (skip call/telecom endpoints - no toasts)
    const skipToastUrls = ['/api/auth/register', '/api/calls/call/'];
    const skipToast = skipToastUrls.some((u) => url.includes(u));
    if (method !== 'GET' && response.data?.message && !skipToast) {
      toast.success(response.data.message);
    }

    return response;
  } catch (error) {
    console.error('Request failed:', {
      status: error.response?.status,
      message: error.response?.data?.message || error.message,
      url: error.config?.url
    });
    
    throw error;
  }
};




/**
 * HTTP Method Handlers
 * Standardized API methods following REST conventions
 */

/**
 * GET request handler
 * @param {string} path - API endpoint path
 * @param {Object} params - Query parameters
 * @returns {Promise} HTTP response promise
 */
export const GET = (path, params = {}) => {
  return request('GET', path, { params });
};

/**
 * POST request handler
 * @param {string} path - API endpoint path
 * @param {Object} payload - Request body data
 * @returns {Promise} HTTP response promise
 */
export const POST = (path, payload = {}) => {
  return request('POST', path, { payload });
};

/**
 * PUT request handler
 * @param {string} path - API endpoint path
 * @param {Object} payload - Request body data
 * @param {boolean} asForm - Convert to FormData
 * @returns {Promise} HTTP response promise
 */
export const PUT = (path, payload = {}, asForm = false) => {
  const finalPayload = asForm ? convertJsonToFormData(payload) : payload;
  return request('PUT', path, { payload: finalPayload });
};

/**
 * PATCH request handler
 * @param {string} path - API endpoint path
 * @param {Object} payload - Request body data
 * @returns {Promise} HTTP response promise
 */
export const PATCH = (path, payload = {}) => {
  return request('PATCH', path, { payload });
};

/**
 * DELETE request handler
 * @param {string} path - API endpoint path
 * @param {Object} payload - Optional request body data
 * @returns {Promise} HTTP response promise
 */
export const DELETE = (path, payload = {}) => {
  return request('DELETE', path, { payload });
};

/**
 * File upload handler
 * @param {string} path - API endpoint path
 * @param {Object} payload - File data
 * @returns {Promise} HTTP response promise
 */
export const Upload = (path, payload = {}) => {
  return request('POST', path, { payload: convertJsonToFormData(payload) });
};

/**
 * Legacy method - use GET instead
 * @deprecated Use GET method instead
 */
export const GETALL = (path, filters = {}) => {
  return GET(path, filters);
};

// Export HTTP Status Codes
export { HTTP_STATUS };

/**
 * Request Interceptor
 * Handles authentication, headers, and request preprocessing
 */
axios.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    
    // Set default headers
    const headers = {
      'Accept': 'application/json',
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    };

    // Set Content-Type for non-FormData requests
    if (!(config.data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Add authorization header (skip for refresh endpoint)
    if (token && typeof config.url === 'string' && !config.url.includes('/auth/refresh')) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    config.headers = { ...config.headers, ...headers };
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


/**
 * Token refresh service for response interceptor.
 * Uses @platform/auth-sdk to handle token refresh on 401 responses.
 */
const _controllerRefreshService = createTokenRefreshService({
  identityCenterUrl: import.meta.env.VITE_IDENTITY_CENTER_URL || 'https://id.digihrms.com',
  onTokensRefreshed: (accessToken, refreshToken) => {
    storeTokens({ accessToken, refreshToken });
  },
  onRefreshFailure: () => {
    clearTokens();
    sessionStorage.removeItem('user_role');
    sessionStorage.removeItem('sidebarMenus');
  },
});

/**
 * RESPONSE INTERCEPTOR
 * Uses @platform/auth-sdk for token refresh on 401 responses.
 */
axios.interceptors.response.use(
  (response) => {
    // Handle successful response
    return response;
  },
  async (error) => {

    // Handle specific error cases
    if (error.response?.status === 402) {
      // Payment Required - Subscription expired
      const message = error.response?.data?.message || 'Your subscription has expired. Please renew to continue using all features.';
      
      // Dispatch custom event for global expiry handling
      window.dispatchEvent(new CustomEvent('subscription-expired', {
        detail: { message }
      }));
      
      toast.error(message);
    } else if (error.response?.status === 401) {
      /*
       * Attempt to refresh the access token when the server returns 401 (Unauthorized).
       * If the refresh succeeds, retry the original request with the new token.
       */
      const originalRequest = error.config;
      
      // Skip refresh if this IS the refresh endpoint or if refresh already failed
      if (originalRequest?.url?.includes('/auth/refresh') || window.__refreshTokenFailed) {
        return Promise.reject(error);
      }
      
      if (originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;

        // Prevent multiple simultaneous refresh requests
        if (!window.__isRefreshingToken) {
          window.__isRefreshingToken = true;
          window.__refreshPromise = (async () => {
            try {
              const refreshToken = getRefreshToken();
              if (!refreshToken) throw new Error('Missing refresh token');
              
              const newAccessToken = await _controllerRefreshService.refreshTokens();
              if (!newAccessToken) throw new Error('Refresh failed');

              return newAccessToken;
            } catch (error) {
              // Mark refresh as failed to prevent further attempts
              window.__refreshTokenFailed = true;
              clearTokens();
              sessionStorage.removeItem('user_role');
              sessionStorage.removeItem('sidebarMenus');
              throw error;
            } finally {
              window.__isRefreshingToken = false;
            }
          })();
        }

        try {
          const newToken = await window.__refreshPromise;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
        } catch (refreshError) {
          // Don't show toast here - AuthContext handles it
          return Promise.reject(refreshError);
        }
      }
    } else if (error.response?.status === 429) {
      // Rate limiting
      // sessionStorage.removeItem('access_token');
      // sessionStorage.removeItem('sidebarMenus');
      // sessionStorage.removeItem('user_role');
      // Cookies.remove('refresh_token');
      toast.error('Too many requests. Please try again later.');
    } else if (error.response?.status >= 500) {
      // Server errors
      // sessionStorage.removeItem('access_token');
      // sessionStorage.removeItem('sidebarMenus');
      // sessionStorage.removeItem('user_role');
      // Cookies.remove('refresh_token');
      toast.error('Server error. Please try again later.');
    }

    // Reject the error with the original error object to preserve stack traces
    return Promise.reject(error);
  }
);
